import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import { Building2, CalendarRange, Columns3, RotateCcw, Rows3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { MAX_DAYS, PERIOD_MODES, WEEKDAYS } from "@/lib/rotaPeriod";

const Pick = ({ value, onChange, items, testId, className = "sm:w-[230px]" }) => (
  <Select value={String(value)} onValueChange={onChange}>
    <SelectTrigger className={`w-full bg-white ${className}`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {items.map(([v, label]) => <SelectItem key={v} value={String(v)} data-testid={`${testId}-${v}`}>{label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;

const LayoutToggle = ({ layout, onChange }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="rota-layout-toggle">
    {[["staff_rows", Rows3, "Staff down the side"], ["staff_cols", Columns3, "Staff along the top"]].map(([id, Icon, label]) => (
      <button key={id} type="button" onClick={() => onChange(id)} data-testid={`rota-layout-${id}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${layout === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
        <Icon className="h-3.5 w-3.5" />{label}
      </button>
    ))}
  </div>
);

const DefaultControls = ({ isDefault, personal, onSaveDefault, onUseDefault }) => (
  <div className="flex w-full flex-wrap items-center gap-2 border-t border-slate-200 pt-2 text-xs text-slate-500" data-testid="rota-default-controls">
    <Building2 className="h-3.5 w-3.5" />
    <span data-testid="rota-default-status">{isDefault ? "This is your business's default rota view" : personal ? "Your own view on this device (differs from the business default)" : "Not the business default"}</span>
    {!isDefault && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onUseDefault} data-testid="rota-use-default-btn"><RotateCcw className="mr-1 h-3 w-3" />Use business default</Button>}
    {!isDefault && <Button size="sm" variant="outline" className="h-7 rounded-full px-3 text-xs" onClick={onSaveDefault} data-testid="rota-save-default-btn">Make this the default for all managers</Button>}
  </div>
);

export const RotaViewBar = ({ view, onChange, isDefault, personal, onSaveDefault, onUseDefault }) => {
  const set = (patch) => onChange({ ...view, ...patch });
  const setCustom = (patch) => {
    const next = { ...view, ...patch };
    if (patch.customStart && next.customEnd < patch.customStart) {
      const keep = differenceInCalendarDays(parseISO(view.customEnd), parseISO(view.customStart));
      next.customEnd = format(addDays(parseISO(patch.customStart), Math.max(keep, 0)), "yyyy-MM-dd");
    }
    const len = differenceInCalendarDays(parseISO(next.customEnd), parseISO(next.customStart)) + 1;
    if (len < 1) return toast.error("The end date must be on or after the start date");
    if (len > MAX_DAYS) return toast.error(`A rota can show up to ${MAX_DAYS} days at a time`);
    onChange(next);
  };
  const isWeeks = ["week", "fortnight", "four_weeks"].includes(view.mode);
  return (
    <div className="no-print mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="rota-view-bar">
      <CalendarRange className="h-4 w-4 text-slate-400" />
      <Pick value={view.mode} onChange={(mode) => set({ mode })} items={PERIOD_MODES.map((m) => [m.id, m.label])} testId="rota-period-select" className="sm:w-[340px]" />
      {isWeeks && <Pick value={view.weekStart} onChange={(v) => set({ weekStart: Number(v) })} testId="rota-week-start-select" className="sm:w-[200px]"
        items={WEEKDAYS.map((d, i) => [i, `Weeks start ${d}`])} />}
      {view.mode === "custom_month" && <Pick value={view.monthDay} onChange={(v) => set({ monthDay: Number(v) })} testId="rota-month-day-select" className="sm:w-[220px]"
        items={Array.from({ length: 31 }, (_, i) => [i + 1, `Starts on the ${ordinal(i + 1)}`])} />}
      {view.mode === "custom" && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="w-full sm:w-[190px]"><DatePicker value={view.customStart} onChange={(v) => setCustom({ customStart: v })} testId="rota-custom-start" /></div>
          <span className="text-xs text-slate-500">to</span>
          <div className="w-full sm:w-[190px]"><DatePicker value={view.customEnd} onChange={(v) => setCustom({ customEnd: v })} testId="rota-custom-end" minDate={view.customStart} /></div>
        </div>
      )}
      <div className="ml-auto"><LayoutToggle layout={view.layout} onChange={(layout) => set({ layout })} /></div>
      <DefaultControls isDefault={isDefault} personal={personal} onSaveDefault={onSaveDefault} onUseDefault={onUseDefault} />
    </div>
  );
};
